import React, { useEffect, useState, useRef } from 'react';
import { OptionalAccessoriesConfiguration } from 'shared/types/product/OptionalAccessoriesConfiguration';
import { AccessorySelection, Accessory } from 'shared/types/product/Variant';
import QuantityInput from 'components/commercetools-ui/organisms/quantity-input';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import Check from 'icons/check';
import { Product, Variant } from 'types/entity/product';
import { useProjectSettings } from 'frontastic/hooks';
import useProduct from 'frontastic/hooks/useProduct';

interface OptionalAccessoriesProps {
  productVariant: Variant;
  setSelectedAccessories: (accessories: AccessorySelection) => void;
  selectedAccessories: AccessorySelection;
}

const OptionalAccessories = ({
  productVariant,
  setSelectedAccessories,
  selectedAccessories,
}: OptionalAccessoriesProps) => {
  if (!productVariant.attributes?.accessoryItem?.length) return null;

  const { query } = useProduct();
  const [accessories, setAccessories] = useState<Record<string, Product[]>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const hasLoadedRef = useRef(false);

  const { data: projectSettings } = useProjectSettings();
  const accessoriesConfiguration = (
    JSON.parse(projectSettings?.accessoriesConfiguration ?? '{}') as OptionalAccessoriesConfiguration
  ).accessoryTypes;

  useEffect(() => {
    hasLoadedRef.current = false;
    setSelectedAccessories({ accessories: [], accessoriesOnly: false });
  }, [productVariant.sku]);

  useEffect(() => {
    if (!accessoriesConfiguration || !productVariant.attributes?.accessoryItem || hasLoadedRef.current) {
      return;
    }

    query({
      productIds: productVariant.attributes?.accessoryItem,
      limit: productVariant.attributes?.accessoryItem?.length,
      includeFacets: false,
    }).then((res) => {
      if (res.isError) {
        console.error(res.error);
        return;
      }
      const items = res?.data?.items || [];

      // Group and sort accessories by type
      const groupedItems = items.reduce((acc: Record<string, Product[]>, item: Product) => {
        const type = item.variants[0].attributes?.crossSellType?.key || '';
        // Skip items with no type or type that doesn't match any configuration
        if (!type || !accessoriesConfiguration?.some((config) => config.type === type)) {
          return acc;
        }
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(item);
        return acc;
      }, {});

      // Sort each group of products by name in ascending order
      Object.keys(groupedItems).forEach((type) => {
        groupedItems[type].sort((a, b) => {
          const nameA = (a.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          const nameB = (b.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          return nameA.localeCompare(nameB, undefined, { numeric: true });
        });
      });

      // Sort by priority from configuration
      const sortedGroupedItems = Object.fromEntries(
        Object.entries(groupedItems).sort((a, b) => {
          const aConfig = accessoriesConfiguration?.find((config) => config.type === a[0]);
          const bConfig = accessoriesConfiguration?.find((config) => config.type === b[0]);
          return (bConfig?.priority || 0) - (aConfig?.priority || 0);
        }),
      ) as Record<string, Product[]>;

      setAccessories(sortedGroupedItems);
      hasLoadedRef.current = true;
    });
  }, [productVariant, accessoriesConfiguration, query]);

  const toggleSection = (type: string) => {
    setExpandedSections({
      ...expandedSections,
      [type]: !expandedSections[type],
    });
  };

  const getAccessoryConfig = (type: string) => {
    return accessoriesConfiguration?.find((config) => config.type === type);
  };

  const handleAccessorySelection = (variant: Variant, product: Product, quantity: number, isChecked: boolean) => {
    let updatedAccessories = [...selectedAccessories.accessories];

    if (isChecked) {
      const existingIndex = updatedAccessories.findIndex((accessory) => accessory.id === variant.sku);
      if (existingIndex >= 0) {
        updatedAccessories[existingIndex].quantity = quantity;
      } else {
        updatedAccessories.push({ id: variant.sku, quantity, variant, product });
      }
    } else {
      updatedAccessories = updatedAccessories.filter((accessory) => accessory.id !== variant.sku);
    }

    setSelectedAccessories({
      ...selectedAccessories,
      accessories: updatedAccessories,
    });
  };

  const AccessoryItem = ({ product }: { product: Product }) => {
    const variant = product.variants[0];
    const price = variant?.price || 0;
    const existingAccessory = selectedAccessories?.accessories?.find((accessory) => accessory?.id === variant?.sku);
    const isSelected = !!existingAccessory;
    const [quantity, setQuantity] = useState(existingAccessory?.quantity || 1);

    useEffect(() => {
      if (existingAccessory) {
        setQuantity(existingAccessory.quantity || 1);
      }
    }, [existingAccessory]);

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      handleAccessorySelection(variant, product, quantity, isChecked);
    };

    const handleQuantityChange = (newQuantity: number) => {
      setQuantity(newQuantity);
      if (isSelected) {
        handleAccessorySelection(variant, product, newQuantity, true);
      }
    };

    return (
      <div className="flex items-center">
        <div className="flex flex-[calc(3/5)] items-center gap-3">
          <form>
            <input
              type="checkbox"
              id={`accessory-${product.productId}`}
              checked={isSelected}
              onChange={handleCheckboxChange}
              className="size-4 cursor-pointer bg-white"
            />
          </form>
          <div
            className="flex grow flex-col hover:cursor-pointer"
            onClick={() => handleAccessorySelection(variant, product, quantity, !isSelected)}
          >
            <label
              htmlFor={`accessory-${product.productId}`}
              className={`${isSelected ? 'font-bold' : 'font-normal'} cursor-pointer text-sm`}
            >
              {product.name}
            </label>
            <span className={`${isSelected ? 'font-bold' : 'font-normal'} text-xs`}>SKU: {variant?.sku}</span>
          </div>
        </div>
        <div className="flex flex-[calc(2/5)] items-center justify-between">
          <QuantityInput
            maxQuantity={999}
            quantity={quantity}
            onChange={handleQuantityChange}
            onIncreaseQuantity={() => handleQuantityChange(quantity + 1)}
            onDecreaseQuantity={() => handleQuantityChange(Math.max(1, quantity - 1))}
          />
          <span className={`${isSelected ? 'font-bold' : 'font-normal'} text-right text-sm`}>
            +{CurrencyHelpers.formatForCurrency(price)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col pt-7">
      {Object.keys(accessories).length > 0 && (
        <h2 className="mb-2 text-center text-base font-normal">Optional Accessories</h2>
      )}
      {Object.entries(accessories).map(([type, products]) => {
        const config = getAccessoryConfig(type);
        const isExpanded = expandedSections[type] || false;

        return (
          <section key={type} className="overflow-hidden">
            <button
              onClick={() => toggleSection(type)}
              className="flex w-full items-center justify-between border-b bg-neutral-250 p-3 text-left"
              aria-expanded={isExpanded}
              aria-controls={`accessory-section-${type}`}
            >
              <h3 className="text-sm font-semibold">{config?.groupTitle || type}</h3>
              <span>{isExpanded ? '−' : '+'}</span>
            </button>

            {isExpanded && (
              <div id={`accessory-section-${type}`} className="py-4">
                {config?.warning && <p className="mb-3 text-xs font-semibold text-accent-red">{config.warning}</p>}
                {type === 'downrod' && productVariant.attributes?.downrodsIncluded && (
                  <div className="mb-3 flex items-center gap-2">
                    <Check className="size-3 fill-green-400" />
                    <p className="text-sm">Downrods included: {productVariant.attributes?.downrodsIncluded}</p>
                  </div>
                )}
                <ul className="space-y-2">
                  {products.map(
                    (product) =>
                      product.productId && (
                        <li key={product.productId} className="py-1">
                          <AccessoryItem product={product} />
                        </li>
                      ),
                  )}
                </ul>
              </div>
            )}
          </section>
        );
      })}
      {selectedAccessories?.accessories?.length > 0 && (
        <form className="mt-4 flex items-center gap-3 rounded-md border p-3">
          <input
            type="checkbox"
            id="accessory-only"
            className="cursor-pointer"
            onChange={(e) =>
              setSelectedAccessories({
                ...selectedAccessories,
                accessoriesOnly: e.target.checked,
              })
            }
            aria-label="Purchase Accessories Only"
          />
          <label htmlFor="accessory-only" className="text-center text-sm font-normal hover:cursor-pointer">
            Purchase Accessories Only
          </label>
        </form>
      )}
    </div>
  );
};

export default OptionalAccessories;
